import React, { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { api } from '../utils/api';
import { useLanguage } from '../utils/i18n';
import {
  Users,
  Building,
  Zap,
  Bell,
  Search,
  TrendingUp,
  LayoutDashboard,
  Palette,
  Settings as SettingsIcon,
  Trash2,
  AlertTriangle,
  X,
  Mail,
  CreditCard,
  Download
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useNotification } from '../utils/NotificationContext';

export default function AdminPanel() {
  const { session, signOut } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [stats, setStats] = useState({ businesses: 0, clients: 0, visits: 0, notifications: 0 });
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, free
  const [deleteModal, setDeleteModal] = useState({ show: false, bizId: null, bizName: '' });
  const [processingId, setProcessingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [visitCardLoading, setVisitCardLoading] = useState(false);

  useEffect(() => {
    // Check if admin email matches
    if (session?.user?.email !== 'wafiyo.co@gmail.com') {
      navigate('/dashboard');
      return;
    }

    async function fetchData() {
      try {
        const [statsData, businessesData] = await Promise.all([
          api.adminGetStats(),
          api.adminGetBusinesses()
        ]);
        setStats(statsData);
        setBusinesses(businessesData);
      } catch (err) {
        console.error('Failed to fetch admin data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [session, navigate]);

  const confirmDelete = async () => {
    const { bizId, bizName } = deleteModal;
    if (!bizId) return;

    try {
      setIsDeleting(true);
      await api.adminDeleteBusiness(bizId);

      // Update local state
      setBusinesses(prev => prev.filter(b => b.id !== bizId));
      setStats(prev => ({
        ...prev,
        businesses: prev.businesses - 1
      }));

      setDeleteModal({ show: false, bizId: null, bizName: '' });
      showNotification('Account deleted successfully.', 'success');
    } catch (err) {
      console.error('Failed to delete account:', err);
      showNotification('Error deleting account: ' + err.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSendReminder = async (id) => {
    try {
      setProcessingId(id);
      const res = await api.adminSendReminder(id);
      showNotification(res.message || 'Reminder email sent!', 'success');
    } catch (err) {
      console.error('Failed to send reminder:', err);
      showNotification('Error sending reminder: ' + err.message, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleGetVisitCard = async () => {
    try {
      setVisitCardLoading(true);
      await api.adminGetVisitCard();
      showNotification('Visit card downloaded! Open it to add to Apple Wallet.', 'success');
    } catch (err) {
      console.error('Failed to download visit card:', err);
      showNotification('Error: ' + err.message, 'error');
    } finally {
      setVisitCardLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === 'trialing') return matchesSearch && b.subscriptionStatus === 'trialing';
    if (filter === 'paying') return matchesSearch && b.subscriptionStatus === 'active';
    return matchesSearch;
  });

  // Only early return if session is absolutely missing (not just loading data)
  if (!session) return (
    <div className="premium-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)' }}>
      <div className="loader-premium"></div>
    </div>
  );

  const AdminStatCard = ({ icon: Icon, value, label, color }) => (
    <div className="glass-card" style={{ padding: '16px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{
          padding: '10px',
          background: `${color}15`,
          borderRadius: '12px',
          color: color
        }}>
          <Icon size={20} />
        </div>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text)' }}>
        {loading ? '...' : value}
      </div>
    </div>
  );

  return (
    <div className="premium-dashboard">
      <header className="fixed-header">
        <div className="nav-brand-group">
          <Logo size={24} />
          <span className="brand-text" style={{ fontSize: 18 }}>Admin Control</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="header-biz-name hide-mobile" style={{ marginRight: '12px' }}>{session?.user?.email}</span>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary)', padding: '4px 12px', background: 'var(--primary-light)', borderRadius: '20px' }}>
            Root Admin
          </span>
        </div>
      </header>

      <nav className="bottom-nav-premium">
        <Link to="/dashboard" className="sidebar-brand-premium">
          <Logo size={28} />
          <span className="sidebar-brand-text">Wafiyo</span>
        </Link>
        <Link to="/dashboard" className="nav-tab-premium">
          <LayoutDashboard size={24} />
          <span>{t('dashboard')}</span>
        </Link>
        <Link to="/dashboard" state={{ tab: 'clients' }} className="nav-tab-premium">
          <Users size={24} />
          <span>{t('clients')}</span>
        </Link>
        <Link to="/dashboard/card-design" className="nav-tab-premium">
          <Palette size={24} />
          <span>{t('design')}</span>
        </Link>
        <Link to="/dashboard" state={{ tab: 'settings' }} className="nav-tab-premium">
          <SettingsIcon size={24} />
          <span>{t('settings')}</span>
        </Link>
      </nav>

      <div className="dashboard-content-scroll" style={{ paddingTop: '100px' }}>
        <div className="container">
          <section className="greeting-section" style={{ marginBottom: '32px' }}>
            <h1 className="greeting-title">Platform Overview</h1>
            <p className="text-muted">Supervise and control all Wafiyo operations.</p>
          </section>

          <div className="admin-stats-grid">
            <AdminStatCard
              icon={Building}
              value={stats.businesses}
              label="Total Businesses"
              color="#1a73e8"
            />
            <AdminStatCard
              icon={Users}
              value={stats.clients}
              label="Active Clients"
              color="#34a853"
            />
            <AdminStatCard
              icon={Zap}
              value={stats.visits}
              label="Total Visits"
              color="#fbbc04"
            />
            <AdminStatCard
              icon={Bell}
              value={stats.notifications}
              label="Push Notifications"
              color="#ea4335"
            />
          </div>

          {/* Visit Card Section */}
          <section className="glass-card visit-card-section" style={{ marginBottom: 32, padding: 0, overflow: 'hidden' }}>
            <div className="visit-card-inner">
              <div className="visit-card-content">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ padding: 10, background: 'rgba(26, 122, 74, 0.15)', borderRadius: 12 }}>
                    <CreditCard size={22} color="#1A7A4A" />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>Mi Tarjeta de Visita</h3>
                </div>
                <p style={{ fontSize: 14, color: '#7ec8a0', lineHeight: 1.6, marginBottom: 20, maxWidth: 380 }}>
                  Descarga tu tarjeta Wafiyo para Apple Wallet. Muéstrala a negocios — escanean el QR para registrarse.
                </p>
                <button
                  onClick={handleGetVisitCard}
                  disabled={visitCardLoading}
                  className="btn-visit-card"
                >
                  {visitCardLoading ? (
                    <>
                      <div className="loader-premium" style={{ width: 18, height: 18, borderWidth: 2 }}></div>
                      <span>Generando...</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                      </svg>
                      <span>Añadir a Apple Wallet</span>
                    </>
                  )}
                </button>
              </div>
              <div className="visit-card-preview">
                <div className="visit-card-mockup">
                  <div className="mockup-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="mockup-logo">W</div>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Wafiyo</span>
                    </div>
                  </div>
                  <div className="mockup-strip">
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>wafiyo.com</span>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Tarjetas de fidelidad digital</span>
                  </div>
                  <div className="mockup-fields">
                    <div>
                      <div style={{ fontSize: 8, color: '#7ec8a0', textTransform: 'uppercase', letterSpacing: 1 }}>Web</div>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>wafiyo.com</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 8, color: '#7ec8a0', textTransform: 'uppercase', letterSpacing: 1 }}>Soporte</div>
                      <div style={{ fontSize: 10, fontWeight: 600 }}>support@wafiyo.com</div>
                    </div>
                  </div>
                  <div className="mockup-aux">
                    <div>
                      <div style={{ fontSize: 7, color: '#7ec8a0', textTransform: 'uppercase', letterSpacing: 1 }}>Oferta</div>
                      <div style={{ fontSize: 9, fontWeight: 600 }}>30 días gratis</div>
                    </div>
                  </div>
                  <div className="mockup-qr">
                    <div style={{ width: 40, height: 40, background: '#fff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0a1a12" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                        <rect x="14" y="14" width="3" height="3" />
                        <rect x="18" y="18" width="3" height="3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="dashboard-section-padding">
            <div className="admin-header-row">
              <h2 className="activity-title-premium" style={{ margin: 0 }}>Registered Businesses</h2>

              <div className="admin-search-group">
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} size={16} />
                  <input
                    type="text"
                    placeholder="Search name..."
                    className="admin-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      padding: '10px 12px 10px 36px',
                      borderRadius: '10px',
                      border: '1px solid var(--border)',
                      fontSize: '14px',
                      width: '100%',
                      outline: 'none'
                    }}
                  />
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    background: 'white',
                    outline: 'none',
                    minWidth: '140px'
                  }}
                >
                  <option value="all">All Plans</option>
                  <option value="trialing">Trialing Only</option>
                  <option value="paying">Paying Only</option>
                </select>
              </div>
            </div>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden', position: 'relative', minHeight: '300px' }}>
              {loading && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  backdropFilter: 'blur(2px)'
                }}>
                  <div className="loader-premium" style={{ width: '32px', height: '32px' }}></div>
                </div>
              )}

              {/* Desktop Table View */}
              <div className="desktop-only" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Business</th>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Owner</th>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Created</th>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Usage</th>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Status</th>
                      <th style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBusinesses.map(biz => (
                      <tr key={biz.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }} className="hover-highlight">
                        <td style={{ padding: '16px' }}>
                          <div style={{ fontWeight: '700', color: 'var(--text)' }}>{biz.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>ID: {biz.id}</div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ fontSize: '14px' }}>{biz.ownerEmail}</div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            {new Date(biz.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', padding: '2px 8px', background: '#e8f0fe', borderRadius: '4px', color: '#1967d2' }}>
                              {biz.clientCount} clients
                            </span>
                            <span style={{ fontSize: '12px', padding: '2px 8px', background: '#fef7e0', borderRadius: '4px', color: '#b06000' }}>
                              {biz.visitCount} visits
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span className={`badge-status-premium ${biz.subscriptionStatus === 'active' || biz.subscriptionStatus === 'trialing' ? 'badge-status-active' : 'badge-status-inactive'}`} style={{ fontSize: '11px' }}>
                            {biz.subscriptionStatus}
                          </span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {(!(biz.subscriptionStatus === 'active' || biz.subscriptionStatus === 'trialing') || biz.ownerEmail === 'wafiyo.co@gmail.com') && (
                              <button
                                onClick={() => handleSendReminder(biz.id)}
                                disabled={processingId === biz.id}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: processingId === biz.id ? 'var(--text-muted)' : 'var(--primary)',
                                  cursor: processingId === biz.id ? 'not-allowed' : 'pointer',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  transition: 'all 0.2s',
                                  opacity: processingId === biz.id ? 0.5 : 1
                                }}
                                className="hover-highlight"
                                title="Send Payment Reminder"
                              >
                                <Mail size={18} style={{ animation: processingId === biz.id ? 'pulse 1.5s infinite' : 'none' }} />
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteModal({ show: true, bizId: biz.id, bizName: biz.name })}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ea4335',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '8px',
                                transition: 'background 0.2s'
                              }}
                              className="hover-danger-bg"
                              title="Delete Account"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column' }}>
                {filteredBusinesses.map(biz => (
                  <div key={biz.id} style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--text)', fontSize: '15px' }}>{biz.name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{biz.ownerEmail}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span className={`badge-status-premium ${biz.subscriptionStatus === 'active' || biz.subscriptionStatus === 'trialing' ? 'badge-status-active' : 'badge-status-inactive'}`} style={{ fontSize: '10px' }}>
                          {biz.subscriptionStatus}
                        </span>
                        {(!(biz.subscriptionStatus === 'active' || biz.subscriptionStatus === 'trialing') || biz.ownerEmail === 'wafiyo.co@gmail.com') && (
                          <button
                            onClick={() => handleSendReminder(biz.id)}
                            disabled={processingId === biz.id}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: processingId === biz.id ? 'var(--text-muted)' : 'var(--primary)',
                              padding: '4px',
                              opacity: processingId === biz.id ? 0.5 : 1,
                              cursor: processingId === biz.id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <Mail size={16} style={{ animation: processingId === biz.id ? 'pulse 1.5s infinite' : 'none' }} />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModal({ show: true, bizId: biz.id, bizName: biz.name })}
                          style={{ background: 'none', border: 'none', color: '#ea4335', padding: '4px' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: '#e8f0fe', borderRadius: '4px', color: '#1967d2' }}>
                        {biz.clientCount} clients
                      </span>
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: '#fef7e0', borderRadius: '4px', color: '#b06000' }}>
                        {biz.visitCount} visits
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                      Registered: {new Date(biz.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>

              {filteredBusinesses.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)' }}>
                  No businesses found matching criteria.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {deleteModal.show && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card glass-card">
            <div className="modal-header-danger">
              <div className="modal-icon-bg-danger">
                <AlertTriangle size={24} color="#ea4335" />
              </div>
              <button
                onClick={() => setDeleteModal({ show: false, bizId: null, bizName: '' })}
                className="modal-close-btn"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body-padding">
              <h3 className="modal-title-danger">Delete Account?</h3>
              <p className="modal-description">
                You are about to permanently delete <strong>{deleteModal.bizName}</strong>.
                This will remove the business profile, all client data, visits, and the owner's Supabase account.
              </p>

              <div className="modal-warning-box">
                This action is irreversible. All data will be lost forever.
              </div>
            </div>

            <div className="modal-footer-flex">
              <button
                onClick={() => setDeleteModal({ show: false, bizId: null, bizName: '' })}
                className="btn-modal-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn-modal-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hover-highlight:hover {
          background: rgba(0,0,0,0.02);
        }
        .text-muted {
          color: var(--text-muted);
          font-size: 15px;
        }
        
        .admin-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .admin-header-row {
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 20px; 
          flex-wrap: wrap; 
          gap: 16px;
        }

        .admin-search-group {
          display: flex; 
          gap: 12px;
          width: 100%;
        }

        .desktop-only {
          display: block;
        }
        .mobile-only {
          display: none;
        }

        @media (min-width: 768px) {
          .admin-search-group {
            width: auto;
          }
        }

        @media (max-width: 768px) {
          .admin-stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          .greeting-title {
            font-size: 24px;
          }
        }

        @media (max-width: 1023px) {
          .desktop-only {
            display: none;
          }
          .mobile-only {
            display: block;
          }
        }

        @media (max-width: 600px) {
          .admin-header-row {
            flex-direction: column;
            align-items: flex-start;
          }
          .admin-search-group {
            flex-direction: column;
          }
          .admin-search-group > div, 
          .admin-search-group > select {
            width: 100% !important;
          }
          .brand-text {
            font-size: 16px !important;
          }
          .fixed-header {
            padding: 0 12px !important;
          }
        }

        @media (max-width: 480px) {
          .admin-stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
          .glass-card {
            padding: 12px 8px !important;
          }
          .metric-main-value {
            font-size: 18px;
          }
          .greeting-title {
            font-size: 20px;
          }
        }

        .hover-danger-bg:hover {
          background: rgba(234, 67, 53, 0.1) !important;
        }

        /* Modal Styles */
        .admin-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        .admin-modal-card {
          width: 100%;
          max-width: 440px;
          padding: 0;
          overflow: hidden;
          background: white;
          animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .modal-header-danger {
          padding: 24px 24px 0;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .modal-icon-bg-danger {
          width: 48px;
          height: 48px;
          background: rgba(234, 67, 53, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .modal-close-btn {
          background: none;
          border: none;
          color: var(--text-dim);
          cursor: pointer;
          padding: 4px;
        }
        .modal-body-padding {
          padding: 20px 24px;
        }
        .modal-title-danger {
          font-size: 20px;
          font-weight: 800;
          color: var(--text);
          margin-bottom: 12px;
        }
        .modal-description {
          font-size: 15px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .modal-warning-box {
          background: #fff5f5;
          border-left: 4px solid #ea4335;
          padding: 12px 16px;
          font-size: 13px;
          color: #b91c1c;
          font-weight: 600;
        }
        .modal-footer-flex {
          padding: 20px 24px;
          background: #f8f9fa;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .btn-modal-secondary {
          padding: 10px 20px;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: white;
          font-weight: 600;
          cursor: pointer;
        }
        .btn-modal-danger {
          padding: 10px 24px;
          border-radius: 10px;
          border: none;
          background: #ea4335;
          color: white;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(234, 67, 53, 0.3);
        }

        /* Visit Card Section */
        .visit-card-section {
          background: linear-gradient(135deg, #0a1a12 0%, #0f2e1d 40%, #1A7A4A 70%, #0a1a12 100%) !important;
          border: 1px solid rgba(26, 122, 74, 0.25) !important;
        }
        .visit-card-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 32px;
          gap: 32px;
        }
        .visit-card-content {
          flex: 1;
        }
        .visit-card-preview {
          flex-shrink: 0;
        }
        .btn-visit-card {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #0a0a0a;
          color: #fff;
          padding: 12px 24px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.15);
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(0,0,0,0.4);
        }
        .btn-visit-card:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(26, 122, 74, 0.35);
          border-color: rgba(26, 122, 74, 0.5);
        }
        .btn-visit-card:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Mockup Card */
        .visit-card-mockup {
          width: 180px;
          background: #0a1a12;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(26, 122, 74, 0.2);
          color: #fff;
          transform: rotate(2deg);
          transition: transform 0.3s ease;
        }
        .visit-card-mockup:hover {
          transform: rotate(0deg) scale(1.03);
        }
        .mockup-header {
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .mockup-logo {
          width: 22px;
          height: 22px;
          background: #1A7A4A;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 12px;
          color: #fff;
        }
        .mockup-strip {
          background: linear-gradient(135deg, #0f2e1d, #1A7A4A, #0a1a12);
          padding: 16px 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }
        .mockup-fields {
          padding: 10px 12px;
          display: flex;
          justify-content: space-between;
          gap: 8px;
        }
        .mockup-aux {
          padding: 4px 12px 8px;
          display: flex;
          justify-content: space-between;
          gap: 8px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .mockup-qr {
          padding: 8px 12px 12px;
          display: flex;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .visit-card-inner {
            flex-direction: column;
            text-align: center;
            padding: 24px;
          }
          .visit-card-content p {
            margin-left: auto;
            margin-right: auto;
          }
          .visit-card-preview {
            order: -1;
          }
          .visit-card-mockup {
            transform: rotate(0deg);
          }
        }
      `}</style>
    </div>
  );
}
